import { useEffect, useRef, useState } from 'react'
import { PexIcon } from '../icons/PexIcon'
import type {
  AdminAuthPresentationSlotRead,
  AuthPresentationSurface,
} from '../../api/generated/model'
import { defaultAuthHero } from '../../features/auth/useAuthPresentation'
import { useT } from '../../i18n'
import { sharedUiI18n } from '../../i18n/components/shared-ui'

type AuthHeroDraft = {
  file: File
  altTextEn: string
  altTextTr: string
  focalX: number
  focalY: number
}

type AuthHeroEditorProps = {
  surface: AuthPresentationSurface
  slot: AdminAuthPresentationSlotRead
  publishing: boolean
  restoring: boolean
  onPublish: (draft: AuthHeroDraft) => Promise<void>
  onRestore: () => Promise<void>
}

export function AuthHeroEditor({
  surface,
  slot,
  publishing,
  restoring,
  onPublish,
  onRestore,
}: AuthHeroEditorProps) {
  const t = useT(sharedUiI18n).authHero
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [draftUrl, setDraftUrl] = useState<string | null>(null)
  const [altTextEn, setAltTextEn] = useState(slot.alt_text_en)
  const [altTextTr, setAltTextTr] = useState(slot.alt_text_tr ?? '')
  const [focalX, setFocalX] = useState(slot.focal_x)
  const [focalY, setFocalY] = useState(slot.focal_y)

  useEffect(() => () => {
    if (draftUrl) URL.revokeObjectURL(draftUrl)
  }, [draftUrl])

  const liveUrl = slot.hero_url ?? defaultAuthHero(surface, 'webp')
  const previewUrl = draftUrl ?? liveUrl
  const hasDraft = Boolean(file)
  const canPublish = Boolean(file && altTextEn.trim() && !publishing)
  const status = slot.uses_default ? t.bundledDefault : t.publishedCustom
  const helper = t.focusHelper(focalX, focalY)

  function chooseFile(selected: File | null) {
    if (!selected) return
    if (draftUrl) URL.revokeObjectURL(draftUrl)
    setFile(selected)
    setDraftUrl(URL.createObjectURL(selected))
    if (!altTextEn.trim()) setAltTextEn(selected.name.replace(/[-_]/g, ' '))
  }

  async function publish() {
    if (!file || !altTextEn.trim()) return
    await onPublish({ file, altTextEn, altTextTr, focalX, focalY })
    if (draftUrl) URL.revokeObjectURL(draftUrl)
    setFile(null)
    setDraftUrl(null)
  }

  return (
    <section className="rounded-[var(--card-radius)] border border-[var(--card-border)] bg-[var(--card-bg)] shadow-[var(--card-shadow)]">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] px-5 py-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-[680] text-[var(--color-text)]">{t.surfaces[surface]}</h2>
            <span className="rounded-full bg-[var(--color-surface-muted)] px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">{status}</span>
            {hasDraft && <span className="rounded-full bg-[var(--color-signal-100)] px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-signal-700)]">{t.unpublishedDraft}</span>}
          </div>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">{t.surfaceNote}</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => inputRef.current?.click()} className="inline-flex min-h-10 items-center gap-2 rounded-[var(--radius-control)] border border-[var(--color-border-strong)] bg-white px-3 text-xs font-semibold text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]">
            <PexIcon name="image" size="xs" tone="inherit" /> {t.replaceImage}
          </button>
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={(event) => chooseFile(event.target.files?.[0] ?? null)} />
        </div>
      </header>

      <div className="grid gap-6 p-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(18rem,0.65fr)]">
        <div>
          <div className="relative aspect-[16/10] overflow-hidden rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface-muted)]">
            <img src={previewUrl} alt={altTextEn} className="h-full w-full object-cover" style={{ objectPosition: `${focalX}% ${focalY}%` }} />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/35 via-transparent to-transparent" />
            <div className="absolute bottom-3 left-3 rounded-md bg-black/75 px-2.5 py-1.5 text-[11px] font-medium text-white">{t.cropLabel(helper)}</div>
          </div>
          <p className="mt-2 text-xs leading-5 text-[var(--color-text-muted)]">{t.previewNote}</p>
        </div>

        <div className="grid content-start gap-4">
          <label className="grid gap-1.5 text-xs font-semibold text-[var(--color-text)]">
            {t.altTextEn}
            <input value={altTextEn} onChange={(event) => setAltTextEn(event.target.value)} className="pex-auth-input min-h-11" required />
            <span className="font-normal text-[var(--color-text-muted)]">{t.altTextEnHint}</span>
          </label>
          <label className="grid gap-1.5 text-xs font-semibold text-[var(--color-text)]">
            {t.altTextTr}
            <input value={altTextTr} onChange={(event) => setAltTextTr(event.target.value)} className="pex-auth-input min-h-11" />
            <span className="font-normal text-[var(--color-text-muted)]">{t.altTextTrHint}</span>
          </label>
          <label className="grid gap-2 text-xs font-semibold text-[var(--color-text)]">
            {t.horizontalFocus} <span className="font-normal text-[var(--color-text-muted)]">{focalX}%</span>
            <input type="range" min="0" max="100" value={focalX} onChange={(event) => setFocalX(Number(event.target.value))} className="accent-[var(--color-action)]" />
          </label>
          <label className="grid gap-2 text-xs font-semibold text-[var(--color-text)]">
            {t.verticalFocus} <span className="font-normal text-[var(--color-text-muted)]">{focalY}%</span>
            <input type="range" min="0" max="100" value={focalY} onChange={(event) => setFocalY(Number(event.target.value))} className="accent-[var(--color-action)]" />
          </label>
        </div>
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--color-border)] px-5 py-4">
        <button type="button" onClick={() => void onRestore()} disabled={restoring || slot.uses_default} className="inline-flex min-h-10 items-center gap-2 rounded-[var(--radius-control)] px-3 text-xs font-semibold text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] disabled:opacity-40">
          <PexIcon name="reset" className="h-[15px] w-[15px]" size="xs" tone="inherit" /> {restoring ? t.restoring : t.restoreDefault}
        </button>
        <button type="button" onClick={() => void publish()} disabled={!canPublish} className="inline-flex min-h-10 items-center gap-2 rounded-[var(--radius-control)] bg-[var(--button-primary-bg)] px-4 text-xs font-semibold text-[var(--button-primary-fg)] disabled:opacity-45">
          <PexIcon name="upload" className="h-[15px] w-[15px]" size="xs" tone="inherit" /> {publishing ? t.publishing : t.savePublish}
        </button>
      </footer>
    </section>
  )
}
